import clsx from "clsx";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Skeleton, Stack, SxProps } from "@mui/material";

// Types
import { IUserState } from "@/Common/interfaces/user";
import { ILocationState } from "@/Washouts/types/PayrollClosing/CommissionCycleClosingSection";
import { IPayPeriod, IStatus } from "@/Common/api/types/CommissionCycle";

// Hooks
import { usePositionsWithChangeRequests } from "@/Washouts/hooks/common/usePositionsWithChangeRequests";
import useAccessPositions from "@/Washouts/hooks/WashoutReview/useAccessPositions";
import { useAllWashoutReviewActions } from "@/Washouts/hooks/WashoutReview/useAllWashoutReviewActions";

// Helpers
import { getAccessPosition } from "@/Washouts/helpers/WashoutReview";
import { isEveryProviderCompleted } from "@/Washouts/helpers/Statuses";

// Layouts
import Button from "@/Layouts/components/Buttons/Button";

// Components
import ApproveWashoutsDialog from "./ApproveWashoutsDialog";

type ApproveAllWashoutButtonProps = {
	commissionCycleId?: string;
	payPeriod?: IPayPeriod;
	partnerPayPeriod?: IPayPeriod | null;
	positionStatuses?: IStatus[];
	disabled: boolean;
	sx?: SxProps;
};

export default function ApproveAllWashoutButton(props: Readonly<ApproveAllWashoutButtonProps>) {
	const { commissionCycleId = "", payPeriod, partnerPayPeriod, disabled, positionStatuses = [], sx } = props;
	const payPeriodId = payPeriod?._id ?? "";
	const accessPositions = useAccessPositions();
	const locationId = useSelector((state: ILocationState) => state.location.location?.locationId ?? "");
	const currentUser = useSelector((state: IUserState) => state.user.user);
	const [openPositionsSelector, setOpenPositionsSelector] = useState(false);
	const { disabledByChangeRequests = [] } = usePositionsWithChangeRequests({
		payPeriodId,
	});

	const { approveAllWashoutsMutation, isPendingApproveAllWashoutMutation } = useAllWashoutReviewActions({
		locationId,
		commissionCycleId,
		payPeriodId,
		currentUser: currentUser?._id ?? "",
	});

	const disabledByAccess = positionStatuses
		?.filter((item) => {
			const positionAccess = getAccessPosition(accessPositions, item.position);
			return !positionAccess?.access;
		})
		.map((item) => item.position);

	const isPeriodCompleted = isEveryProviderCompleted(payPeriod);
	const disabledPositions = [...new Set([...disabledByChangeRequests, ...disabledByAccess])];
	const isDisabledApprove = disabled || isPeriodCompleted || !positionStatuses?.length;

	return (
		<>
			{isPendingApproveAllWashoutMutation ? (
				<Skeleton
					variant="rectangular"
					width={143.36}
					height={30}
					sx={{
						borderRadius: 1.5,
					}}
				/>
			) : (
				<>
					<Stack>
						<Button
							variant="contained"
							classes={clsx({
								"h-[30px] !shadow-none !font-sans !capitalize !text-white !text-[14px]": true,
								"!bg-primary hover:!shadow-primary-shadow-50": !isDisabledApprove,
								"!bg-disabled !cursor-default": isDisabledApprove,
							})}
							text={"Approve All Washouts"}
							disabled={isDisabledApprove}
							sx={{ minWidth: "133px", ...sx }}
							onClick={() => setOpenPositionsSelector(true)}
						/>
					</Stack>

					<ApproveWashoutsDialog
						commissionCycleId={commissionCycleId}
						payPeriod={payPeriod}
						partnerPayPeriod={partnerPayPeriod}
						openPositionsSelector={openPositionsSelector}
						positionStatuses={positionStatuses}
						disabledPositions={disabledPositions}
						setOpenPositionsSelector={setOpenPositionsSelector}
						approveWashouts={approveAllWashoutsMutation}
					/>
				</>
			)}
		</>
	);
}
